import { useQuery } from '@tanstack/react-query';
import { emailAdminService, emailOutboxService, emailTemplateService } from '@/services/emailAdminService';
import type { EmailSmtpConfig, EmailTemplate, OutboundEmail } from '@/types/emailAdmin';
import { queryKeys } from '@/lib/queryKeys';
import { useAuth } from '@/hooks/useAuth';
import { API_CONFIG } from '@/config/api';

export const useSmtpConfig = () => {
  const { isAuthenticated } = useAuth();
  return useQuery<EmailSmtpConfig | null>({
    queryKey: queryKeys.notifications.emailAdmin.smtpConfig(),
    queryFn: () => emailAdminService.getSmtpConfig(),
    enabled: isAuthenticated,
    // Always refetch on mount so previously saved settings hydrate the form even when the
    // query cache was persisted with older data.
    refetchOnMount: 'always',
    staleTime: 0,
  });
};

export const useOutboundEmails = (options: { limit?: number; refetchIntervalMs?: number } = {}) => {
  const { isAuthenticated } = useAuth();
  const limit = options.limit ?? 50;

  return useQuery<OutboundEmail[]>({
    queryKey: queryKeys.notifications.emailAdmin.outbox(limit),
    queryFn: () => emailOutboxService.list(limit),
    enabled: isAuthenticated,
    staleTime: API_CONFIG.CACHE_STALE_TIME_MS,
    refetchInterval: options.refetchIntervalMs ?? false,
  });
};

export const useEmailTemplates = () => {
  const { isAuthenticated } = useAuth();
  return useQuery<EmailTemplate[]>({
    queryKey: queryKeys.notifications.emailAdmin.templates(),
    queryFn: () => emailTemplateService.list(),
    enabled: isAuthenticated,
    refetchOnMount: 'always',
    staleTime: 0,
  });
};
